"use client";

import { useState } from "react";
import { FormLayout } from "../FormLayout";
import { ImageUpload } from "../ImageUpload";
import { ContactForm } from "./sections/ContactForm";
import { ClassifiedPreview } from "./sections/ClassifiedPreview";

export function RoommateForm({
  step,
  maxSteps,
  onNext,
  onBack,
  onClose,
  formData,
  updateFormData,
  isProcessing,
}) {
  const [title, setTitle] = useState(formData.title || "");
  const [description, setDescription] = useState(formData.description || "");
  const [images, setImages] = useState(formData.images || []);
  const [genderPreference, setGenderPreference] = useState(
    formData.genderPreference || ""
  );
  const [ageRange, setAgeRange] = useState(formData.ageRange || "");
  const [moveInDate, setMoveInDate] = useState(formData.moveInDate || "");
  const [leaseDuration, setLeaseDuration] = useState(
    formData.leaseDuration || ""
  );
  const [lifestyle, setLifestyle] = useState(formData.lifestyle || "");
  const [price, setPrice] = useState(formData.price || "");
  const [story, setStory] = useState(formData.story || "");

  // Validation errors state
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateStep = (stepNumber) => {
    const newErrors = {};

    switch (stepNumber) {
      case 1: // Photos
        if (images.length < 6) {
          newErrors.images = "At least 6 photos are required";
        }
        break;
      case 2: // Title
        if (!title.trim()) {
          newErrors.title = "Title is required";
        } else if (title.trim().length < 2) {
          newErrors.title = "Title must be at least 2 characters long";
        }
        break;
      case 3: // Key Details
        if (!genderPreference) {
          newErrors.genderPreference = "Gender preference is required";
        }
        if (!ageRange) {
          newErrors.ageRange = "Age range is required";
        }
        if (!moveInDate) {
          newErrors.moveInDate = "Move-in date is required";
        }
        if (!leaseDuration) {
          newErrors.leaseDuration = "Lease duration is required";
        }
        break;
      case 4: // Price
        if (!price) {
          newErrors.price = "Monthly rent budget is required";
        } else if (price <= 0) {
          newErrors.price = "Budget must be greater than 0";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Determine if Next button should be disabled
  const isNextDisabled = () => {
    switch (step) {
      case 1: // Photos
        return images.length < 6;
      case 2: // Title
        return !title.trim() || title.trim().length < 2;
      case 3: // Key Details
        return !genderPreference || !ageRange || !moveInDate || !leaseDuration;
      case 4: // Price
        return !price || price <= 0;
      case 6: // Contact Form
        return (
          !formData.isContactFormValid ||
          !formData.fullName?.trim() ||
          !formData.email?.trim() ||
          !formData.phoneNumber?.trim() ||
          !formData.addressLine1?.trim() ||
          !formData.state?.trim()
        );
      default:
        return false;
    }
  };

  // Save data when moving between steps
  const handleNext = () => {
    if (!validateStep(step)) {
      return;
    }

    if (step === 3) {
      const specific_details = {
        genderPreference,
        ageRange,
        moveInDate,
        leaseDuration,
        lifestyle,
      };
      updateFormData({
        ...formData,
        specific_details,
      });
    } else if (step === 6) {
      updateFormData({
        ...formData,
        contactName: formData.contactName || "",
        contactEmail: formData.contactEmail || "",
        contactPhone: formData.contactPhone || "",
        preferredContact: formData.preferredContact || "",
        address: formData.address || "",
        city: formData.city || "",
        state: formData.state || "",
        zip: formData.zip || "",
        agreeTerms: formData.agreeTerms || false,
      });
    } else {
      updateFormData({
        title,
        description,
        images,
        genderPreference,
        ageRange,
        moveInDate,
        leaseDuration,
        lifestyle,
        price: Number(price),
        story,
      });
    }
    onNext();
  };

  const handleImagesChange = (newImages) => {
    setImages(newImages);
    if (newImages.length > 0 && errors.images) {
      setErrors((prev) => ({ ...prev, images: undefined }));
    }
  };

  const inputClassName =
    "w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-[#7B189F] transition-colors";
  const errorInputClassName =
    "w-full p-4 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors";

  const renderStepContent = () => {
    switch (step) {
      case 1: // Add Photos
        return (
          <div className="space-y-4">
            <ImageUpload
              category="Roommate"
              minPhotos={6}
              images={images}
              onChange={handleImagesChange}
            />
            {errors.images && (
              <p className="text-red-500 text-sm mt-1">{errors.images}</p>
            )}
          </div>
        );

      case 2: // Add Title
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 font-fraunces"
              >
                Add title for your roommate listing
              </label>
              <input
                id="title"
                type="text"
                placeholder="e.g., Looking for a clean, friendly roommate"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: undefined }));
                  }
                }}
                className={errors.title ? errorInputClassName : inputClassName}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>
          </div>
        );

      case 3: // Key Details
        return (
          <div className="space-y-5">
            <h2 className="text-xl font-medium font-fraunces">
              Add key details about your roommate preferences
            </h2>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Gender Preference *
              </label>
              <div className="flex gap-6">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="no-preference"
                    value="no-preference"
                    checked={genderPreference === "no-preference"}
                    onChange={(e) => {
                      setGenderPreference(e.target.value);
                      if (errors.genderPreference) {
                        setErrors((prev) => ({
                          ...prev,
                          genderPreference: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="no-preference"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    No Preference
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="male"
                    value="male"
                    checked={genderPreference === "male"}
                    onChange={(e) => {
                      setGenderPreference(e.target.value);
                      if (errors.genderPreference) {
                        setErrors((prev) => ({
                          ...prev,
                          genderPreference: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="male"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Male
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="female"
                    value="female"
                    checked={genderPreference === "female"}
                    onChange={(e) => {
                      setGenderPreference(e.target.value);
                      if (errors.genderPreference) {
                        setErrors((prev) => ({
                          ...prev,
                          genderPreference: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="female"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Female
                  </label>
                </div>
              </div>
              {errors.genderPreference && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.genderPreference}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <label
                htmlFor="ageRange"
                className="block text-sm font-medium text-gray-700"
              >
                Preferred Age Range *
              </label>
              <select
                id="ageRange"
                value={ageRange}
                onChange={(e) => {
                  setAgeRange(e.target.value);
                  if (errors.ageRange) {
                    setErrors((prev) => ({ ...prev, ageRange: undefined }));
                  }
                }}
                className={
                  errors.ageRange ? errorInputClassName : inputClassName
                }
              >
                <option value="">Select age range</option>
                <option value="18-22">18-22</option>
                <option value="23-27">23-27</option>
                <option value="28-32">28-32</option>
                <option value="33-37">33-37</option>
                <option value="38+">38+</option>
                <option value="any">Any Age</option>
              </select>
              {errors.ageRange && (
                <p className="text-red-500 text-sm mt-1">{errors.ageRange}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <label
                  htmlFor="moveInDate"
                  className="block text-sm font-medium text-gray-700"
                >
                  Move-in Date *
                </label>
                <input
                  id="moveInDate"
                  type="date"
                  value={moveInDate}
                  onChange={(e) => {
                    setMoveInDate(e.target.value);
                    if (errors.moveInDate) {
                      setErrors((prev) => ({ ...prev, moveInDate: undefined }));
                    }
                  }}
                  className={
                    errors.moveInDate ? errorInputClassName : inputClassName
                  }
                />
                {errors.moveInDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.moveInDate}
                  </p>
                )}
              </div>
              <div className="space-y-3">
                <label
                  htmlFor="leaseDuration"
                  className="block text-sm font-medium text-gray-700"
                >
                  Lease Duration *
                </label>
                <select
                  id="leaseDuration"
                  value={leaseDuration}
                  onChange={(e) => {
                    setLeaseDuration(e.target.value);
                    if (errors.leaseDuration) {
                      setErrors((prev) => ({
                        ...prev,
                        leaseDuration: undefined,
                      }));
                    }
                  }}
                  className={
                    errors.leaseDuration ? errorInputClassName : inputClassName
                  }
                >
                  <option value="">Select duration</option>
                  <option value="3-months">3 Months</option>
                  <option value="6-months">6 Months</option>
                  <option value="1-year">1 Year</option>
                  <option value="flexible">Flexible</option>
                </select>
                {errors.leaseDuration && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.leaseDuration}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <label
                htmlFor="lifestyle"
                className="block text-sm font-medium text-gray-700"
              >
                Lifestyle Preferences
              </label>
              <select
                id="lifestyle"
                value={lifestyle}
                onChange={(e) => setLifestyle(e.target.value)}
                className={inputClassName}
              >
                <option value="">Select lifestyle</option>
                <option value="non-smoking">Non-smoking</option>
                <option value="pet-friendly">Pet-friendly</option>
                <option value="quiet">Quiet/Studious</option>
                <option value="social">Social/Outgoing</option>
                <option value="clean">Very Clean</option>
                <option value="flexible">Flexible</option>
              </select>
            </div>
          </div>
        );

      case 4: // Price
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium font-fraunces">
              Set your monthly rent budget
            </h2>
            <h4 className="text-sm font-medium font-afacad text-gray-500">
              What's your budget for monthly rent (including utilities if
              applicable)?
            </h4>
            <div className="space-y-2">
              <label
                htmlFor="price"
                className="block text-sm font-medium text-gray-700"
              >
                Monthly Rent Budget *
              </label>
              <input
                id="price"
                type="number"
                min="0"
                step="0.01"
                placeholder="$ Enter monthly amount"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  if (errors.price) {
                    setErrors((prev) => ({ ...prev, price: undefined }));
                  }
                }}
                className={errors.price ? errorInputClassName : inputClassName}
              />
              {errors.price && (
                <p className="text-red-500 text-sm mt-1">{errors.price}</p>
              )}
            </div>
          </div>
        );

      case 5: // Tell story
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium font-fraunces">
              Tell us about yourself and what you're looking for
            </h2>
            <p className="text-sm text-gray-500">
              Share details about yourself and your ideal living situation!
            </p>
            <div className="space-y-2">
              <label
                htmlFor="story"
                className="block text-sm font-medium text-gray-700"
              >
                About yourself / What you're looking for
              </label>
              <textarea
                id="story"
                rows={6}
                placeholder="Tell potential roommates about yourself, your habits, and what kind of living situation you're seeking..."
                value={story}
                onChange={(e) => setStory(e.target.value)}
                className={inputClassName}
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700"
              >
                Additional Details
              </label>
              <textarea
                id="description"
                rows={6}
                placeholder="Any additional information about the living space, location, or requirements..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={inputClassName}
              />
            </div>
          </div>
        );

      case 6: // Contact details
        return (
          <ContactForm formData={formData} updateFormData={updateFormData} />
        );
      case 7: // Preview
        return <ClassifiedPreview formData={formData} category="Roommate" />;
      default:
        return null;
    }
  };

  // Get step-specific title and description
  const getStepInfo = () => {
    const title = "Create Classified";
    let description = "";

    switch (step) {
      case 1:
        description = "Add photos of yourself or the space";
        break;
      case 2:
        description = "Add title and description";
        break;
      case 3:
        description = "Add key details";
        break;
      case 4:
        description = "Set budget";
        break;
      case 5:
        description = "Tell your story";
        break;
      case 6:
        description = "Add your contact details";
        break;
      case 7:
        description = "Preview your classified";
        break;
    }

    return { title, description };
  };

  const { title: stepTitle, description: stepDescription } = getStepInfo();

  return (
    <FormLayout
      title={stepTitle}
      description={stepDescription}
      step={step}
      maxSteps={maxSteps}
      onNext={handleNext}
      onBack={onBack}
      onClose={onClose}
      isLastStep={step === maxSteps}
      isNextDisabled={isNextDisabled()}
      isProcessing={isProcessing}
    >
      {renderStepContent()}
    </FormLayout>
  );
}
